/**
 * LifecycleAuditDrawer — read-only, right-aligned drawer that shows
 * the immutable audit timeline for one Agentic Network pool.
 *
 * Powered by the M9 endpoint
 * ``GET /networks/{id}/pools/{poolId}/lifecycle-events`` and the pure
 * ``projectLifecycleAuditRow`` projection so JSX stays declarative.
 *
 * Open / close is fully controlled — the LifecycleTab owns the
 * ``targetPool`` state and clears it on dismissal. Closing from the
 * X button, the backdrop, or pressing Escape are all wired here so
 * the parent doesn't have to remember keyboard handling.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cpu, History, RefreshCw, User, X } from 'lucide-react';
import { listPoolLifecycleEvents } from '@stepflow/ui-shared/services/poolLifecycleApi.js';
import {
  projectLifecycleAuditRow,
} from '@stepflow/ui-shared/utils/lifecycleAuditFormat.js';

const PAGE_SIZE = 100;

export default function LifecycleAuditDrawer({
  networkId,
  pool,
  open,
  onClose,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [events, setEvents] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(0);

  const poolId = pool?.poolId || null;
  const poolLabel = pool?.displayName
    || pool?.poolId
    || (poolId ? String(poolId) : '');

  const fetchAudit = useCallback(async () => {
    if (!networkId || !poolId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await listPoolLifecycleEvents(networkId, poolId, {
        limit: PAGE_SIZE,
        newestFirst: true,
      });
      setEvents(Array.isArray(res?.events) ? res.events : []);
      setFetchedAt(Date.now());
    } catch (e) {
      setError(e?.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [networkId, poolId]);

  useEffect(() => {
    if (open) fetchAudit();
  }, [open, fetchAudit]);

  // Escape closes — the rest of the workbench is read-only behind
  // the drawer so there's nothing dangerous to escape *to*.
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') onClose?.();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Snapshot the wall clock at fetch-time, not at render-time. Calling
  // ``Date.now()`` inside ``useMemo`` violates the React purity rules
  // (``react-hooks/purity``) — and a re-render triggered for any
  // unrelated reason would otherwise repaint every "x minutes ago"
  // label, which is also UX-noisy. ``fetchedAt`` is set once per
  // request, so the timeline labels are stable until the next fetch.
  // The first render before the initial fetch settles passes
  // ``undefined`` so the helper falls back to ``Date.now()`` from
  // outside any React hook (which is the React-purity rule's actual
  // concern — module helpers can read the clock freely).
  const projected = useMemo(() => {
    const ref = fetchedAt > 0 ? fetchedAt : undefined;
    return events
      .map((evt) => projectLifecycleAuditRow(evt, ref))
      .filter(Boolean);
  }, [events, fetchedAt]);

  if (!open || !pool) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="audit-backdrop"
        className="fixed inset-0 z-40 bg-black/30"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        aria-hidden
      />
      <motion.aside
        key="audit-panel"
        role="dialog"
        aria-modal="true"
        aria-label={`Lifecycle audit for ${poolLabel}`}
        className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-white shadow-xl"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'tween', duration: 0.2 }}
      >
        <header className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <History className="h-4 w-4 text-gray-500" aria-hidden />
              Lifecycle audit
            </h2>
            <p
              className="mt-0.5 truncate text-xs text-gray-600"
              title={poolLabel}
            >
              {poolLabel}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={fetchAudit}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              aria-label="Reload audit log"
              title="Reload"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`}
                aria-hidden
              />
              Reload
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-gray-500 hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </header>

        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 py-4">
          {error && (
            <p
              className="mb-3 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700"
              role="alert"
            >
              {error}
            </p>
          )}

          {loading && events.length === 0 && (
            <p className="text-xs text-gray-500">Loading audit timeline&hellip;</p>
          )}

          {!loading && events.length === 0 && !error && (
            <p className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-6 text-center text-xs text-gray-500">
              No lifecycle transitions recorded yet — the timeline will
              fill in once this pool is paused, retired, or auto-moved
              by the nightly sweeper.
            </p>
          )}

          {projected.length > 0 && (
            <ol className="space-y-3">
              {projected.map((row, idx) => (
                <AuditRow
                  key={`${row.raw.occurredAt}-${row.raw.sequence || idx}`}
                  row={row}
                />
              ))}
            </ol>
          )}

          {fetchedAt > 0 && events.length > 0 && (
            <p className="mt-4 text-[10px] text-gray-400">
              Fetched {events.length} of up to {PAGE_SIZE} most recent
              transitions at {new Date(fetchedAt).toLocaleTimeString()}.
            </p>
          )}
        </div>
      </motion.aside>
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
// One audit row
// ---------------------------------------------------------------------------

function AuditRow({ row }) {
  const ActorIcon = row.actorRole === 'person' ? User : Cpu;
  return (
    <li className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium ${row.badgeClass}`}
        >
          {row.transitionLabel}
        </span>
        <span className="text-[11px] text-gray-500">{row.timeAgo}</span>
      </div>
      {row.detail && (
        <p className="mt-1.5 text-xs text-gray-600">{row.detail}</p>
      )}
      <p
        className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-gray-500"
        title={row.raw.actor || ''}
      >
        <ActorIcon className="h-3 w-3" aria-hidden />
        {row.actorLabel}
      </p>
    </li>
  );
}
